import React from "react";
import { View } from "react-native";
import Placeholder from "../../general/Placeholder/Placeholder";
import AiTutorCardProgress from "../AiTutorCardProgress/AiTutorCardProgress";
import AxioResourceCard from "../../customCoursework/AxioResourceCard/AxioResourceCard";
import { MotiView } from "moti";
import { Link, FolderUser, ArrowsClockwise, Sparkle, FileText } from "@phosphor-icons/react";
import AssessmentCard from "../../customCoursework/AssessmentCard/AssessmentCard";
import { useNavigation, useRoute } from "@react-navigation/native";
import { useAppConfig } from "../../../AppConfigProvider";

function AiTutorCardProgressList({ items = [], onPress, onHoverIn, onHoverOut, isInitialLoad = false }) {
  const navigation = useNavigation();
  const route = useRoute();

  const { primaryColor } = useAppConfig();

  const { course_id: courseId, integration_id: integrationId, module_id: moduleId } = route.params;

  const resourceTypeToIcon = {
    axioResource: <Link width={24} height={24} fill={primaryColor} />,
    aiTutor: <Sparkle width={24} height={24} fill={primaryColor} />,
    assignment: <FileText width={24} height={24} fill={primaryColor} />,
    review: <ArrowsClockwise size={24} color={primaryColor} />,
    project: <FolderUser width={24} height={24} fill={primaryColor} />,
  };

  const handleAssessmentButtonPress = (id) => {
    navigation.navigate("Custom Course Assessment", {
      assessment_id: id,
      course_id: courseId,
      integration_id: integrationId,
      module_id: moduleId,
    });
  };

  const renderResourceCard = (item, index) => {
    const icon = resourceTypeToIcon[item.resourceType];

    const animationConfig = isInitialLoad
      ? {
          from: {
            opacity: 0,
            translateY: 20,
            scale: 0.95,
          },
          animate: {
            opacity: 1,
            translateY: 0,
            scale: 1,
          },
          delay: 300 + index * 300,
          transition: { type: "timing", duration: 800 },
        }
      : {
          from: {
            opacity: 1,
            translateY: 0,
            scale: 1,
          },
          animate: {
            opacity: 1,
            translateY: 0,
            scale: 1,
          },
          delay: 0,
          transition: { type: "timing", duration: 0 },
        };

    if (item.resourceType === "axioResource" || item.resourceType === "assignment" || item.resourceType === "project") {
      return (
        <MotiView key={item.id} {...animationConfig}>
          <AxioResourceCard
            title={item.details.title}
            description={item.details.description}
            videoUrl={item.details.videoUrl}
            icon={icon}
            files={item.files}
            isHovered={item.isHovered}
            onPress={() => onPress(item.id)}
            onHoverIn={() => onHoverIn(item.id)}
            onHoverOut={() => onHoverOut(item.id)}
            dueDate={item.dueDate}
            activityState={item.activityState}
            scheduledDate={item.scheduledDate}
            isPending={item.details?.isPending}
            isComplete={item.details?.isComplete}
            grade={item.details?.grade}
          />
        </MotiView>
      );
    }

    if (item.resourceType === "assessment") {
      return (
        <MotiView key={item.id} {...animationConfig}>
          <AssessmentCard
            onButtonPress={() => handleAssessmentButtonPress(item.id)}
            grade={item.latestAssessmentSubmission?.totalPoints}
            status={item.latestAssessmentSubmission?.status}
            isEvaluationPending={item.latestAssessmentSubmission?.isEvaluationPending}
            onPress={() => onPress(item?.id, null, item?.latestAssessmentSubmission?.id)}
            onHoverIn={() => onHoverIn(item.id)}
            onHoverOut={() => onHoverOut(item.id)}
            isHovered={item.isHovered}
            maximumPoints={item.latestAssessmentSubmission?.maximumPoints}
            activityState={item.activityState}
            scheduledDate={item.scheduledDate}
          />
        </MotiView>
      );
    }

    // Handle both aiTutor and review types with AiTutorCardProgress
    return (
      <MotiView key={item.id} {...animationConfig}>
        <AiTutorCardProgress
          title={item.title}
          isHovered={item.isHovered}
          isComplete={item.details?.isComplete}
          totalObjectives={item.details?.totalObjectives}
          icon={icon}
          completedObjectives={item.details?.completedObjectives}
          dueDate={item.dueDate}
          onPress={() => onPress(item.id, item.aiTutorIdOfReview)} // Pass aiTutorId for reviews
          onHoverIn={() => onHoverIn(item.id)}
          onHoverOut={() => onHoverOut(item.id)}
          isReviewContentBeingGenerated={item.isReviewContentBeingGenerated}
          activityState={item.activityState}
          scheduledDate={item.scheduledDate}
        />
      </MotiView>
    );
  };

  return (
    <View>
      {items.length > 0 ? (
        <>{items.map(renderResourceCard)}</>
      ) : (
        <View style={{ flex: 1, justifyContent: "center" }}>
          <Placeholder subtitle="Visit your Learning Management System to access these resources" />
        </View>
      )}
    </View>
  );
}

export default AiTutorCardProgressList;
